import React, { useState } from 'react';
import axios from 'axios';
import { useMercenary } from '../../contexts/MercenaryContext';
import styles from './IssueReportForm.module.css';

interface IssueReportFormProps {
  onClose: () => void;
  onSubmit?: () => void;
}

const IssueReportForm: React.FC<IssueReportFormProps> = ({ onClose, onSubmit }) => {
  const { mercenary } = useMercenary();
  const [formData, setFormData] = useState({
    issueType: 'bug',
    priority: 'medium',
    title: '',
    description: '',
    stepsToReproduce: '',
    expectedBehavior: '',
    actualBehavior: '',
    pageUrl: window.location.href
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  const issueTypes = [
    { value: 'bug', label: 'Bug Report' },
    { value: 'game_mechanic', label: 'Game Mechanic Issue' },
    { value: 'balance', label: 'Balance Issue' },
    { value: 'feature_request', label: 'Feature Request' },
    { value: 'ui_ux', label: 'UI/UX Issue' },
    { value: 'performance', label: 'Performance Issue' },
    { value: 'other', label: 'Other' }
  ];

  const priorities = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'critical', label: 'Critical' }
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.description.trim()) {
      setSubmitMessage({ type: 'error', text: 'Title and description are required.' });
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage(null);

    try {
      await axios.post('/api/issues/report', {
        ...formData,
        mercenaryId: mercenary?.id
      }, { withCredentials: true });

      setSubmitMessage({ type: 'success', text: 'Issue reported successfully! Thank you for your feedback.' });
      
      // Reset form
      setFormData({
        issueType: 'bug',
        priority: 'medium',
        title: '',
        description: '',
        stepsToReproduce: '',
        expectedBehavior: '',
        actualBehavior: '',
        pageUrl: window.location.href
      });

      if (onSubmit) {
        onSubmit();
      }

      // Auto-close after success
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Error submitting issue report:', error);
      setSubmitMessage({ type: 'error', text: 'Failed to submit issue report. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2>Report an Issue</h2>
          <button className={styles.closeButton} onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="issueType">Issue Type *</label>
              <select
                id="issueType"
                name="issueType"
                value={formData.issueType}
                onChange={handleInputChange}
                required
              >
                {issueTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="priority">Priority</label>
              <select
                id="priority"
                name="priority"
                value={formData.priority}
                onChange={handleInputChange}
              >
                {priorities.map(priority => (
                  <option key={priority.value} value={priority.value}>
                    {priority.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="title">Title *</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Brief description of the issue"
              required
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="description">Description *</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Detailed description of the issue"
              rows={4}
              required
            />
          </div>

          {formData.issueType === 'bug' && (
            <>
              <div className={styles.formGroup}>
                <label htmlFor="stepsToReproduce">Steps to Reproduce</label>
                <textarea
                  id="stepsToReproduce"
                  name="stepsToReproduce"
                  value={formData.stepsToReproduce}
                  onChange={handleInputChange}
                  placeholder="1. Go to...&#10;2. Click on...&#10;3. See error"
                  rows={3}
                />
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label htmlFor="expectedBehavior">Expected Behavior</label>
                  <textarea
                    id="expectedBehavior"
                    name="expectedBehavior"
                    value={formData.expectedBehavior}
                    onChange={handleInputChange}
                    placeholder="What should happen?"
                    rows={2}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="actualBehavior">Actual Behavior</label>
                  <textarea
                    id="actualBehavior"
                    name="actualBehavior"
                    value={formData.actualBehavior}
                    onChange={handleInputChange}
                    placeholder="What actually happens?"
                    rows={2}
                  />
                </div>
              </div>
            </>
          )}

          <div className={styles.formGroup}>
            <label htmlFor="pageUrl">Page URL</label>
            <input
              type="url"
              id="pageUrl"
              name="pageUrl"
              value={formData.pageUrl}
              onChange={handleInputChange}
              placeholder="URL where the issue occurred"
            />
          </div>

          {submitMessage && (
            <div className={`${styles.message} ${styles[submitMessage.type]}`}>
              {submitMessage.text}
            </div>
          )}

          <div className={styles.formActions}>
            <button type="button" className={styles.cancelButton} onClick={onClose}>
              Cancel
            </button>
            <button 
              type="submit" 
              className={styles.submitButton} 
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Report'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default IssueReportForm;
